import {
  GlassContentScope,
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { cn } from "@-/com.npmjs.cn";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { useState } from "react";
import { LearnFlashcardActionButton } from "#learn-card-action-button";
import { LearnFlashcardLessonMenu } from "#learn-card-lesson-menu";

/**
 * One full-width lesson pill, with a separate menu section and a decorative right chevron. On a
 * revealed card it reads "Next Pronunciation" — opening the next reading the lesson introduces —
 * until every introduced reading has been viewed (by this button or the binder tab), then "Next
 * Card".
 */
export function LearnFlashcardLessonControls({ isFront = false }: { isFront?: boolean }) {
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const [commit, isTransitioning, lessonControls, switchView] = useContextSelectors(
    LearnDeckContext,
    (c) => c.commit,
    (c) => c.isTransitioning,
    (c) => (c.cardControls?.kind === "lesson" ? c.cardControls : null),
    (c) => c.switchView,
  );
  const showsNextPronunciation = lessonControls !== null && lessonControls.hasUnviewedPronunciation;
  const isPronunciationLesson = lessonControls?.isPronunciationLesson === true;
  // A lesson card whose controls have not arrived yet is LOADING, not inert: its surfaces can be
  // mounted (and the card fully painted) while the subtree owning the reveal is still suspended.
  // Showing that state — rather than a silently dead pill — keeps the row honest, and the deck's
  // watchdog recovers the card if it never resolves.
  const isAwaitingControls = isFront && lessonControls === null;
  // The system menu on iOS sizes its host to the trigger, so the square trigger takes the pill's
  // measured height rather than `h-full`.
  const [pillHeight, setPillHeight] = useState(0);

  return (
    // The chevron and the menu overlay the action button's glass, so they share its ink.
    <GlassContentScope>
      <View
        className="relative size-full rounded-full"
        onLayout={({ nativeEvent: { layout } }) => {
          setPillHeight(layout.height);
        }}
      >
        <View className="size-full">
          <LearnFlashcardActionButton
            action={{ kind: "accept-lesson" }}
            className={isLandscape ? "px-0" : "px-11"}
            disabled={isAwaitingControls}
            handlesAllActions
            icon={isFront ? "LuEye" : showsNextPronunciation ? "RiSpeakLine" : "LuChevronRight"}
            label={isFront ? "Reveal" : showsNextPronunciation ? "Next Pronunciation" : "Next Card"}
            onPress={() => {
              if (isFront) {
                // The pill is disabled while the controls are awaited.
                lessonControls?.reveal();
                return;
              }
              if (showsNextPronunciation) {
                // The reading opens on the Learn view, so bring it forward from Details first.
                switchView("study");
                lessonControls.viewNextPronunciation();
                return;
              }
              commit({ kind: "accept-lesson" }, "button");
            }}
            swipeDirection="both"
            testID={
              isFront
                ? "lesson-new-card-reveal"
                : showsNextPronunciation
                  ? "lesson-view-pronunciation"
                  : "lesson-next-card"
            }
          />
        </View>
        {!isLandscape ? (
          <View
            accessibilityElementsHidden
            className="absolute inset-y-0 right-0 aspect-square h-full items-center justify-center"
            importantForAccessibility="no-hide-descendants"
            pointerEvents="none"
          >
            <Icon
              className={
                isTransitioning || isAwaitingControls ? "text-neutral-400" : "text-foreground"
              }
              name="LuChevronRight"
              size={20}
            />
          </View>
        ) : null}
        <View
          className={
            isLandscape
              ? "absolute inset-x-0 top-0 overflow-hidden rounded-full"
              : "absolute inset-y-0 left-0 overflow-hidden rounded-l-full"
          }
        >
          {pillHeight === 0 ? null : (
            <LearnFlashcardLessonMenu
              isDisabled={isTransitioning}
              // A pronunciation-level lesson marks nothing on the character's meaning card.
              onMarkKnown={
                isPronunciationLesson ? null : () => commit({ kind: "mark-known" }, "button")
              }
              onSkip={() =>
                commit(
                  isPronunciationLesson ? { kind: "continue" } : { kind: "skip-lesson" },
                  "button",
                )
              }
              trigger={
                <Pressable
                  accessibilityLabel="More card actions"
                  accessibilityRole="button"
                  accessibilityState={{ disabled: isTransitioning }}
                  className={cn(
                    "items-center justify-center overflow-hidden",
                    isLandscape ? "rounded-full" : "rounded-l-full border-r border-border",
                    !isTransitioning && "active:bg-accent",
                    isTransitioning && "cursor-not-allowed",
                  )}
                  disabled={isTransitioning}
                  style={{
                    height: isLandscape ? diameter : pillHeight,
                    width: isLandscape ? diameter : pillHeight,
                  }}
                  testID="lesson-card-menu"
                >
                  {isLandscape ? (
                    <ToolbarGlassSurface
                      className="absolute inset-0"
                      interactive={false}
                      pointerEvents="none"
                    />
                  ) : null}
                  <Icon
                    className={isTransitioning ? "text-neutral-400" : "text-foreground"}
                    name="FiMoreHorizontal"
                    size={20}
                  />
                </Pressable>
              }
            />
          )}
        </View>
      </View>
    </GlassContentScope>
  );
}
